import { Text, Button, Flex, Stack, ThemeIcon, Title } from '@mantine/core';
import * as Sentry from '@sentry/react';
import { IconAlertCircle, IconError404 } from '@tabler/icons-react';
import * as React from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';

import { ROUTES } from '@/configs/routes';
import { useResponsiveProp } from '@/hooks/useResponsiveProp';

import s from './ErrorPage.module.scss';
const ErrorPage: React.FC = () => {
  const error = useRouteError() as { status?: number };

  React.useEffect(() => {
    if (isRouteErrorResponse(error)) {
      const { status, statusText, data } = error;

      Sentry.captureException(new Error(statusText ?? 'Route error'), {
        tags: { type: 'route-error', status: status?.toString() },
        extra: { data },
      });

      return;
    }

    Sentry.captureException(new Error('Unknown route error'), {
      tags: { type: 'route-error' },
      extra: { error },
    });
  }, [error]);

  const isNotFound = error.status === 404;

  const align = useResponsiveProp({ desktop: 'center', phone: 'flex-start' });
  const textAlign = useResponsiveProp({ desktop: 'center', phone: 'left' });
  const size = useResponsiveProp({ desktop: 'lg', phone: 'md' });
  const titleOrder = useResponsiveProp({ desktop: 1, phone: 2 });

  return (
    <Flex justify="center" className={s.root}>
      <div className={s.root__container}>
        <Stack gap="xl">
          <Stack gap="lg" align={align}>
            <ThemeIcon radius="md" size="xl" variant="outline" color="gray">
              {isNotFound ? (
                <IconError404 color="var(--mantine-color-text)" />
              ) : (
                <IconAlertCircle color="var(--mantine-color-text)" />
              )}
            </ThemeIcon>

            <Stack align={align} gap="sm">
              <Title order={titleOrder} variant="text" ta={textAlign}>
                {isNotFound ? 'Ошибка 404' : 'Произошла ошибка'}
              </Title>
              <Text ta={textAlign} c="gray">
                {isNotFound
                  ? 'Страница не найдена или не существует. Возможно, вы ошиблись при вводе адреса в строку в браузере.'
                  : 'Нам очень жаль, уже работаем над исправлениями'}
              </Text>
            </Stack>
          </Stack>
          <Button fullWidth size={size} component={Link} to={ROUTES.root.create()}>
            На главную
          </Button>
        </Stack>
      </div>
    </Flex>
  );
};

export default ErrorPage;
